import React from 'react';
import { Dialog } from '@/shared/components/Dialog';
import { Textarea } from '@/shared/components/TextField';
import cancelRequestIllustration from '@/assets/svg/cancel-request-illustration.svg?url';
import {
  CANCEL_SERVICE_REQUEST_LABELS as L,
  UPDATE_SR_STATUS_LABELS,
} from '../../constants/labels';

/** Props for {@link CancelServiceRequestModal}. All state is managed by {@link useCancelServiceRequest}. */
interface CancelServiceRequestModalProps {
  /** Whether the modal is visible. */
  open: boolean;
  /** Closes the modal; no-op while `isSubmitting` is `true`. */
  onClose: () => void;
  /** Current cancellation reason text. */
  reason: string;
  /** Called on every keystroke to update the reason. */
  onReasonChange: (value: string) => void;
  /** Inline validation error for the reason field; empty string when valid. */
  reasonError: string;
  /** `true` while the cancel API call is in flight; disables inputs and buttons. */
  isSubmitting: boolean;
  /** API-level error message displayed below the textarea. */
  submitError: string;
  /** `true` when the confirm button should be interactive. */
  canSubmit: boolean;
  /** Triggers form validation then the cancel API call. */
  onSubmit: () => void;
}

const CancelServiceRequestModal: React.FC<CancelServiceRequestModalProps> = ({
  open,
  onClose,
  reason,
  onReasonChange,
  reasonError,
  isSubmitting,
  submitError,
  canSubmit,
  onSubmit,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={388}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      data-testid="cancel-sr-modal"
    >
      <div className="flex flex-col items-center gap-6 p-6">
        <img
          src={cancelRequestIllustration}
          alt=""
          aria-hidden="true"
          className="w-[137px] h-[137px] shrink-0"
        />

        <div className="flex flex-col items-center gap-4 w-full">
          <h2
            className="text-[#474A4C] font-normal text-center w-full"
            style={{ fontSize: 18, lineHeight: '26px' }}
            data-testid="cancel-sr-modal-title"
          >
            {L.MODAL_TITLE}
          </h2>

          <p
            className="text-[#474A4C] text-center w-full"
            style={{
              fontSize: 14,
              lineHeight: '20px',
              letterSpacing: '0.25px',
            }}
            data-testid="cancel-sr-description"
          >
            {L.DESCRIPTION}
          </p>

          <div className="w-full" data-field="cancel-sr-reason">
            <Textarea
              label={L.FIELD_REASON}
              placeholder={L.PLACEHOLDER_REASON}
              value={reason}
              onChange={(e) =>
                onReasonChange((e.target as HTMLTextAreaElement).value ?? '')
              }
              maxLength={UPDATE_SR_STATUS_LABELS.NOTES_MAX_LENGTH}
              rows={4}
              fullWidth
              error={Boolean(reasonError)}
              errorMessage={reasonError}
              disabled={isSubmitting}
              dataTestId="cancel-sr-reason-input"
            />
          </div>

          {submitError && (
            <p
              className="text-error-40 w-full"
              style={{ fontSize: 12, lineHeight: '16px' }}
              data-testid="cancel-sr-submit-error"
            >
              {submitError}
            </p>
          )}
        </div>

        <div className="flex items-center justify-center gap-3 w-full">
          {/* Wróć — full 48px touch target, border wraps full height */}
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={`w-[140px] h-10 flex items-stretch rounded-lg border border-[#DCDDE0] overflow-hidden transition-colors ${
              isSubmitting
                ? 'bg-[rgba(24,26,27,0.1)] cursor-not-allowed'
                : 'bg-white hover:bg-[#F3F4F6] cursor-pointer'
            }`}
            data-testid="cancel-sr-back-button"
          >
            <div
              className={`flex-1 flex items-center justify-center px-4 py-2.5 ${isSubmitting ? 'opacity-[0.38]' : ''}`}
            >
              <span
                className={`text-sm font-medium leading-5 tracking-[0.1px] ${isSubmitting ? 'text-[#181A1B]' : 'text-[#474A4C]'}`}
              >
                {L.BUTTON_BACK}
              </span>
            </div>
          </button>

          {/* Anuluj — 48px touch target, 40px visual pill inside */}
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit || isSubmitting}
            className={`min-w-35 h-12 flex items-center justify-center ${
              !canSubmit || isSubmitting
                ? 'cursor-not-allowed'
                : 'cursor-pointer'
            }`}
            data-testid="cancel-sr-confirm-button"
          >
            <div
              className={`w-full h-10 flex items-center justify-center px-4 rounded-lg transition-colors ${
                canSubmit && !isSubmitting
                  ? 'bg-[#2A4455] hover:bg-[#1f3340]'
                  : 'bg-[rgba(24,26,27,0.1)]'
              }`}
            >
              <span
                className={`text-sm font-medium leading-5 tracking-[0.1px] whitespace-nowrap ${
                  canSubmit && !isSubmitting
                    ? 'text-white'
                    : 'text-[#181A1B] opacity-[0.38]'
                }`}
              >
                {isSubmitting ? L.BUTTON_CONFIRMING : L.BUTTON_CONFIRM}
              </span>
            </div>
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default CancelServiceRequestModal;
